import { PropsWithChildren, useEffect, useState } from "react";
import {
  formatDate,
  formatDateRange,
  formatDateTime,
  formatTime,
} from "../UI/Utils/formatDate.ts";
import dayjs from "dayjs";
import { Layout } from "../UI/Layout/Layout.tsx";
import { WeeklyCalendar } from "../Calendar/WeeklyCalendar.tsx";
import { Interval } from "../Calendar/Interval.tsx";
import { createTodayInterval } from "../Calendar/createTodayInterval.ts";
import { Card } from "../UI/Card.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { Modal } from "../UI/Modal/Modal.tsx";
import { useCheckCalendlyAvailability } from "../Calendly/useCheckCalendlyAvailability.ts";
import { useExportCalendlyAvailability } from "../Calendly/useExportCalendlyAvailability.ts";
import {
  AvailableUserDTO,
  useGetCalendlyUsers,
} from "../Calendly/useGetCalendlyUsers.ts";
import { MultipleSelect, Select } from "@normasteaching/norma-ui";
import { useCheckHubSpotCloserAvailability } from "../HubSpot/useCheckHubSpotCloserAvailability.ts";
import { useExportHubSpotCloserAvailability } from "../HubSpot/useExportHubSpotCloserAvailability.ts";
import {
  HubSpotSalesMemberDTO,
  useGetHubSpotSalesMembers,
} from "../HubSpot/useGetHubSpotSalesMembers.ts";
import {
  HubSpotSalesGroupDTO,
  useGetHubSpotSalesGroups,
} from "../HubSpot/useGetHubSpotSalesGroups.ts";
import {
  CloserAvailabilityNormalizedSlot,
  createCloserAvailabilityCalendarMap,
} from "./createCloserAvailabilityCalendarMap.ts";
import { CloserAvailabilityCalendarSlotItem } from "./CloserAvailabilityCalendarSlotItem.tsx";
import { MisalignedSlotsModal } from "./MisalignedSlotsModal.tsx";
import { SchedulerClosersModal } from "./SchedulerClosersModal.tsx";
import { useCloserIssueIndicators } from "./useCloserIssueIndicators.ts";
import { ExportCsvButton } from "../UI/ExportCsvButton.tsx";
import { IconButton } from "../UI/IconButton.tsx";
import {
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon,
  UserGroupIcon,
} from "@heroicons/react/24/outline/index.js";
import { useNavigate } from "react-router-dom";
import { CloserAvailabilityHistoryScreenPath } from "./History/closerAvailabilityHistoryPath.ts";
import { CloserStatsScreenPath } from "./Stats/closerStatsPath.ts";
import { AssignmentsScreenPath } from "./Assignments/assignmentsPath.ts";
import {
  CloserSlotTotals,
  countTotalsOfDay,
  hasNoTotals,
  sumCloserSlots,
} from "./closerSlotTotals.ts";

export const CheckClosersAvailabilityScreenPath =
  "/closers/check-closers-availability";

type Source = "calendly" | "hubspot";

function busyWorkCategoryLabel(workCategory?: string): string {
  switch (workCategory) {
    case "in_call":
      return "In Call";
    default:
      return "Lavoro";
  }
}

function nameFromEmail(email: string): string {
  return email
    .split("@")[0]
    .split(".")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

const today = dayjs().format("YYYY-MM-DD");

function formatRangeLabel(startDate: string, endDate: string): string {
  if (startDate === endDate) return formatDate(startDate, { locale: "it" });
  return formatDateRange(startDate, endDate, { locale: "it" });
}

const DATE_INPUT_CLASS_NAME =
  "w-full min-w-0 md:w-auto px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

function FilterField({
  label,
  className,
  children,
}: PropsWithChildren<{ label: string; className?: string }>) {
  return (
    <div className={`space-y-1 ${className ?? ""}`}>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {children}
    </div>
  );
}

function SlotTotalsRow({ totals }: { totals: CloserSlotTotals }) {
  return (
    <span className="flex items-center gap-1.5 text-xs tabular-nums">
      <span className="text-green-600 font-medium">
        {totals.availableCount} slots
      </span>
      <span className="text-gray-300">·</span>
      <span className="text-blue-500 font-medium">
        {totals.callCount} calls
      </span>
      <span className="text-gray-300">·</span>
      <span className="text-gray-400 font-medium">{totals.oooCount} ooo</span>
    </span>
  );
}

export function CheckClosersAvailabilityScreen() {
  const navigate = useNavigate();
  const [source] = useState<Source>("hubspot");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groups, setGroups] = useState<HubSpotSalesGroupDTO[]>([]);
  const [slots, setSlots] = useState<CloserAvailabilityNormalizedSlot[]>([]);
  const [searchedRange, setSearchedRange] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);
  const [displayInterval, setDisplayInterval] = useState<Interval>(
    createTodayInterval(),
  );
  const [selectedSlot, setSelectedSlot] = useState<
    CloserAvailabilityNormalizedSlot | undefined
  >();
  const [error, setError] = useState<string | null>(null);
  const [showMisalignedSlots, setShowMisalignedSlots] = useState(false);
  const [showSchedulerClosers, setShowSchedulerClosers] = useState(false);
  const [calendlyUsers, setCalendlyUsers] = useState<AvailableUserDTO[]>([]);
  const [hubspotMembers, setHubspotMembers] = useState<HubSpotSalesMemberDTO[]>(
    [],
  );

  const { checkCalendlyAvailability, loading: loadingCalendly } =
    useCheckCalendlyAvailability();
  const { checkHubSpotCloserAvailability, loading: loadingHubSpot } =
    useCheckHubSpotCloserAvailability();
  const { exportCalendlyAvailability, loading: loadingCalendlyExport } =
    useExportCalendlyAvailability();
  const { exportHubSpotCloserAvailability, loading: loadingHubSpotExport } =
    useExportHubSpotCloserAvailability();
  const { getCalendlyUsers } = useGetCalendlyUsers();
  const { getHubSpotSalesMembers } = useGetHubSpotSalesMembers();
  const { getHubSpotSalesGroups } = useGetHubSpotSalesGroups();
  const { hasMisalignedSlots, hasCloserIssues } = useCloserIssueIndicators();

  const loading = source === "calendly" ? loadingCalendly : loadingHubSpot;
  const loadingExport =
    source === "calendly" ? loadingCalendlyExport : loadingHubSpotExport;

  useEffect(() => {
    getCalendlyUsers().then(setCalendlyUsers).catch(console.error);
  }, [getCalendlyUsers]);

  useEffect(() => {
    getHubSpotSalesMembers()
      .then((m) => setHubspotMembers(m ?? []))
      .catch(console.error);
  }, [getHubSpotSalesMembers]);

  useEffect(() => {
    getHubSpotSalesGroups().then(setGroups).catch(console.error);
  }, [getHubSpotSalesGroups]);

  const onChangeStartDate = (value: string) => {
    setStartDate(value);
    if (dayjs(endDate).isBefore(dayjs(value))) setEndDate(value);
  };

  const onChangeEndDate = (value: string) => {
    setEndDate(value);
    if (dayjs(value).isBefore(dayjs(startDate))) setStartDate(value);
  };

  const handleSearch = async () => {
    setError(null);
    if (groupHasNoMembers) {
      setError("The selected group has no members.");
      return;
    }
    const fromIncl = dayjs(startDate).tz("Europe/Rome").startOf("day").toDate();
    const toIncl = dayjs(endDate).tz("Europe/Rome").endOf("day").toDate();

    try {
      let result: CloserAvailabilityNormalizedSlot[];

      if (source === "calendly") {
        const data = await checkCalendlyAvailability({
          fromIncl,
          toIncl,
          filteredUsers: effectiveFilterEmails,
        });
        result = (data ?? []).map((s) => ({
          slotStart: s.slotStart,
          slotEnd: s.slotEnd,
          availableUsers: s.availableUsers.map((u) => ({
            id: u.uri,
            email: u.email,
          })),
        }));
      } else {
        const data = await checkHubSpotCloserAvailability({
          fromIncl,
          toIncl,
          filteredEmails: effectiveFilterEmails,
        });
        result = (data ?? []).map((s) => ({
          slotStart: s.slotStart,
          slotEnd: s.slotEnd,
          availableUsers: s.availableUsers.map((u) => ({
            id: u.id,
            email: u.email,
          })),
          busyWorkUsers: s.busyWorkUsers.map((b) => ({
            id: b.user.id,
            email: b.user.email,
            workCategory: b.workCategory,
            eventTitle: b.eventTitle,
          })),
          busyOooUsers: s.busyOooUsers.map((b) => ({
            id: b.user.id,
            email: b.user.email,
            eventTitle: b.eventTitle,
          })),
        }));
      }

      setSlots(result);
      setSearchedRange({ startDate, endDate });
      const from = dayjs(startDate);
      setDisplayInterval(
        new Interval(from.startOf("week"), from.endOf("week")),
      );

      if (result.length === 0) {
        setError("No availability found for the selected date range.");
      }
    } catch (err) {
      console.error("Error fetching availability:", err);
      setError("Error fetching availability data. Please try again later.");
    }
  };

  const handleExport = async () => {
    setError(null);
    const fromIncl = dayjs(startDate).tz("Europe/Rome").startOf("day").toDate();
    const toIncl = dayjs(endDate).tz("Europe/Rome").endOf("day").toDate();
    try {
      if (source === "calendly") {
        await exportCalendlyAvailability({
          fromIncl,
          toIncl,
          filteredUsers: effectiveFilterEmails,
        });
      } else {
        await exportHubSpotCloserAvailability({
          fromIncl,
          toIncl,
          filteredEmails: effectiveFilterEmails,
        });
      }
    } catch (err) {
      console.error("Error exporting:", err);
    }
  };

  const onGroupChange = (groupId: string | null) => {
    setSelectedGroupId(groupId);
    setSelectedUsers([]);
  };

  const groupMemberEmails = selectedGroupId
    ? new Set(
        (groups.find((g) => g.id === selectedGroupId)?.members ?? []).map((m) =>
          m.email.toLowerCase(),
        ),
      )
    : null;

  const groupHasNoMembers =
    selectedGroupId !== null && (groupMemberEmails?.size ?? 0) === 0;

  const effectiveFilterEmails =
    selectedGroupId !== null && selectedUsers.length === 0
      ? [...(groupMemberEmails ?? [])]
      : selectedUsers;

  const memberOptions = (
    source === "calendly"
      ? calendlyUsers.map((u) => ({
          id: u.email,
          label: nameFromEmail(u.email),
          description: u.email,
        }))
      : hubspotMembers
          .filter(
            (m) =>
              !groupMemberEmails ||
              groupMemberEmails.has(m.email.toLowerCase()),
          )
          .map((m) => ({
            id: m.email,
            label: nameFromEmail(m.email),
            description: m.email,
          }))
  ).sort((a, b) => a.id.localeCompare(b.id));

  const calendarItems = createCloserAvailabilityCalendarMap(slots);
  const rangeTotals = sumCloserSlots(slots);

  return (
    <>
      <MisalignedSlotsModal
        isOpen={showMisalignedSlots}
        onClose={() => setShowMisalignedSlots(false)}
      />

      <SchedulerClosersModal
        isOpen={showSchedulerClosers}
        onClose={() => setShowSchedulerClosers(false)}
      />

      <Modal
        isOpen={!!selectedSlot}
        onClose={() => setSelectedSlot(undefined)}
        title={
          selectedSlot
            ? `${formatDateTime(selectedSlot.slotStart, { locale: "it" })} – ${formatTime(selectedSlot.slotEnd)}`
            : ""
        }
      >
        {selectedSlot &&
          (() => {
            const busyWorkCount = selectedSlot.busyWorkUsers?.length ?? 0;
            const busyOooCount = selectedSlot.busyOooUsers?.length ?? 0;
            const busyTotal = busyWorkCount + busyOooCount;
            return (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                    Disponibili{" "}
                    <span className="text-gray-500 normal-case font-normal">
                      ({selectedSlot.availableUsers.length})
                    </span>
                  </p>
                  {selectedSlot.availableUsers.length === 0 ? (
                    <p className="text-sm text-slate-400">
                      Nessuno disponibile.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {[...selectedSlot.availableUsers]
                        .sort((a, b) => a.email.localeCompare(b.email))
                        .map((u) => (
                          <div
                            key={u.id}
                            className="p-2 rounded-md bg-white border border-gray-200"
                          >
                            <p className="text-sm font-medium">
                              {nameFromEmail(u.email)}
                            </p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {busyTotal > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                      Occupati{" "}
                      <span className="text-gray-500 normal-case font-normal">
                        ({busyTotal} — In call: {busyWorkCount}, OOO:{" "}
                        {busyOooCount})
                      </span>
                    </p>
                    <div className="space-y-1">
                      {[
                        ...(selectedSlot.busyWorkUsers ?? []).map((u) => ({
                          ...u,
                          kind: "work" as const,
                        })),
                        ...(selectedSlot.busyOooUsers ?? []).map((u) => ({
                          ...u,
                          kind: "ooo" as const,
                        })),
                      ]
                        .sort((a, b) => a.email.localeCompare(b.email))
                        .map((u) => (
                          <div
                            key={`${u.kind}-${u.id}`}
                            className="p-2 rounded-md bg-gray-100"
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-600">
                                {nameFromEmail(u.email)}
                              </p>
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded ${
                                  u.kind === "ooo"
                                    ? "bg-gray-200 text-gray-500"
                                    : "bg-blue-100 text-blue-600"
                                }`}
                              >
                                {u.kind === "ooo"
                                  ? "OOO"
                                  : busyWorkCategoryLabel(u.workCategory)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400">{u.email}</p>
                            {u.eventTitle && (
                              <p className="text-xs text-gray-400 italic mt-0.5">
                                {u.eventTitle}
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
      </Modal>

      <Layout maxWClass="max-w-full">
        <div className="mb-4">
          <Card>
            <div className="flex items-center justify-between gap-3">
              <div className="hidden md:flex items-center gap-3">
                <h1 className="font-bold text-2xl">
                  Check Closer Availability
                </h1>
                <span className="px-3 py-1.5 rounded bg-orange-500 text-white text-sm font-medium">
                  Scheduler
                </span>
              </div>

              <div className="flex w-full flex-wrap items-center justify-between gap-2 md:w-auto md:flex-nowrap md:justify-start">
                {source === "hubspot" && (
                  <div className="flex items-center gap-2">
                    <IconButton
                      label="Slot non allineati"
                      icon={ExclamationTriangleIcon}
                      onClick={() => setShowMisalignedSlots(true)}
                      hasAlert={hasMisalignedSlots}
                    />
                    <IconButton
                      label="Storico"
                      icon={ClockIcon}
                      onClick={() =>
                        navigate(CloserAvailabilityHistoryScreenPath)
                      }
                    />
                    <IconButton
                      label="Statistiche"
                      icon={ChartBarIcon}
                      onClick={() => navigate(CloserStatsScreenPath)}
                    />
                    <IconButton
                      label="Assegnazioni"
                      icon={Squares2X2Icon}
                      onClick={() => navigate(AssignmentsScreenPath)}
                    />
                    <IconButton
                      label="Closer dello scheduler"
                      icon={UserGroupIcon}
                      onClick={() => setShowSchedulerClosers(true)}
                      hasAlert={hasCloserIssues}
                    />
                  </div>
                )}
                <div className="ml-auto md:ml-0">
                  <ExportCsvButton
                    loading={loadingExport}
                    onClick={handleExport}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div className="flex flex-col gap-3 md:flex-row md:items-end">
                <div className="grid grid-cols-2 gap-3 md:flex md:items-end">
                  <FilterField label="From" className="min-w-0">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => onChangeStartDate(e.target.value)}
                      className={DATE_INPUT_CLASS_NAME}
                    />
                  </FilterField>
                  <FilterField label="To" className="min-w-0">
                    <input
                      type="date"
                      value={endDate}
                      min={startDate}
                      max={dayjs(startDate)
                        .add(2, "month")
                        .format("YYYY-MM-DD")}
                      onChange={(e) => onChangeEndDate(e.target.value)}
                      className={DATE_INPUT_CLASS_NAME}
                    />
                  </FilterField>
                </div>
                {source === "hubspot" && (
                  <FilterField label="Gruppo" className="w-full md:w-44">
                    <Select
                      clearable
                      placeholder="Tutti i gruppi"
                      value={selectedGroupId ?? undefined}
                      onChange={onGroupChange}
                      options={groups.map((g) => ({ id: g.id, name: g.name }))}
                    />
                  </FilterField>
                )}
                <FilterField label="Closer" className="w-full md:w-56">
                  <MultipleSelect
                    overlay
                    searchable
                    placeholder="Tutti i closer"
                    value={selectedUsers}
                    onChange={(values) =>
                      setSelectedUsers(values.includes("All") ? [] : values)
                    }
                    options={[
                      { id: "All", label: `All (${memberOptions.length})` },
                      ...memberOptions,
                    ]}
                  />
                </FilterField>
              </div>

              <PrimaryButton
                loading={loading}
                onClick={handleSearch}
                label="Check Availability"
                className="justify-center"
              />
            </div>

            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}
          </Card>
        </div>

        <WeeklyCalendar
          interval={displayInterval}
          items={calendarItems}
          loading={false}
          onChangeInterval={setDisplayInterval}
          canGoToPrevWeek={true}
          rowGapClass="gap-y-0"
          periodSummary={
            searchedRange && slots.length > 0 ? (
              <div className="flex flex-col leading-tight">
                <span className="text-xs font-medium text-slate-600">
                  {formatRangeLabel(
                    searchedRange.startDate,
                    searchedRange.endDate,
                  )}
                </span>
                <SlotTotalsRow totals={rangeTotals} />
              </div>
            ) : undefined
          }
          daySubtitle={(day) => {
            const totals = countTotalsOfDay(calendarItems, day);
            if (hasNoTotals(totals)) return undefined;
            return <SlotTotalsRow totals={totals} />;
          }}
          itemRenderer={(group) => (
            <CloserAvailabilityCalendarSlotItem
              group={group}
              onClick={(slot) => setSelectedSlot(slot)}
            />
          )}
          emptyDayComponent={
            <p className="text-center text-slate-400 text-sm">
              No availability
            </p>
          }
        />
      </Layout>
    </>
  );
}
